"use client";

import { Link } from "@/lib/i18n/navigation";
import { CalendarCheck, CalendarBlank, CaretRight, VideoCamera, MapPin, Clock } from "@phosphor-icons/react/dist/ssr";
import { Container } from "@/components/ui/Container";
import { useUpcomingAppointments } from "./useUpcomingAppointments";

export type AppointmentsLabels = {
  title: string;
  viewAll: string;
  trackTitle: string;
  trackSub: string;
  emptyTitle: string;
  historySub: string;
};

/**
 * Widget "mes prochains RDV" — version desktop de la homepage.
 * SSR affiche toujours l'état "non connecté" (safe à cacher côté CDN) ; ce
 * composant fetch /api/mon-compte/upcoming-appointments après montage et
 * affiche les vrais RDV du patient une fois la session confirmée cliente.
 */
export function DesktopUpcomingAppointments({ labels }: { labels: AppointmentsLabels }) {
  const { loading, session, appointments } = useUpcomingAppointments();

  return (
    <section aria-label={labels.title} className="hidden bg-[var(--sahha-surface-1)] pb-12 md:block">
      <Container size="2xl">
        <div className="mb-4 flex items-end justify-between">
          <h2 className="text-[22px] font-extrabold tracking-tight text-[var(--sahha-text-900)]">{labels.title}</h2>
          {session && appointments.length > 0 && (
            <Link
              href="/mon-compte/rendez-vous"
              className="text-[14px] font-semibold text-[var(--sahha-primary-700)] hover:underline"
            >
              {labels.viewAll}
            </Link>
          )}
        </div>

        {!session || loading ? (
          <Link
            href="/connexion"
            className="flex items-center gap-4 rounded-[20px] bg-white px-5 py-5 ring-1 ring-[var(--sahha-border-subtle)] transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-100)] text-[var(--sahha-primary-700)]">
              <CalendarCheck weight="duotone" size={26} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-bold leading-tight text-[var(--sahha-text-900)]">{labels.trackTitle}</span>
              <span className="mt-0.5 block text-[13px] text-[var(--sahha-text-500)]">{labels.trackSub}</span>
            </span>
            <CaretRight weight="bold" size={18} className="shrink-0 text-[var(--sahha-text-400)]" />
          </Link>
        ) : appointments.length === 0 ? (
          <Link
            href="/mon-compte/rendez-vous"
            className="flex items-center gap-4 rounded-[20px] bg-white px-5 py-5 ring-1 ring-[var(--sahha-border-subtle)] transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-100)] text-[var(--sahha-primary-700)]">
              <CalendarBlank weight="duotone" size={26} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-bold leading-tight text-[var(--sahha-text-900)]">{labels.emptyTitle}</span>
              <span className="mt-0.5 block text-[13px] text-[var(--sahha-text-500)]">{labels.historySub}</span>
            </span>
            <CaretRight weight="bold" size={18} className="shrink-0 text-[var(--sahha-text-400)]" />
          </Link>
        ) : (
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {appointments.slice(0, 3).map((a) => (
              <li key={a.id}>
                <Link
                  href="/mon-compte/rendez-vous"
                  className="flex items-center gap-3 rounded-[20px] bg-white p-4 ring-1 ring-[var(--sahha-border-subtle)] transition-all hover:-translate-y-0.5 hover:shadow-md"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-100)] text-[14px] font-extrabold text-[var(--sahha-primary-800)]">
                    {a.initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-extrabold leading-tight text-[var(--sahha-text-900)]">{a.doctor}</p>
                    <p className="mt-0.5 truncate text-[12px] text-[var(--sahha-text-600)]">{a.specialty}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] font-semibold text-[var(--sahha-text-500)]">
                      <span className="inline-flex items-center gap-1 text-[var(--sahha-primary-700)]">
                        <Clock weight="fill" size={11} />
                        {a.when} · {a.time}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        {a.type === "video" ? <VideoCamera weight="fill" size={11} /> : <MapPin weight="fill" size={11} />}
                        {a.location}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Container>
    </section>
  );
}
